import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginButtons, type Provider } from '../../../components/auth/login/LoginButtons'

import { useErrorHandler } from '@/contexts/ErrorContext'
import { getWithRedirectApi, getNativeTokenCallbackApi } from '@/api/authApi'
import { isWebView, signInWithGoogleNative, signInWithAppleNative, isIOSWebView } from '@/utils/webView'
import { checkAuthStatus } from '@/utils/auth'
import { logger } from '@/utils/logger'
import { useAuthStore } from '@/store/authStore'

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const { showErrorToast } = useErrorHandler()
  const navigate = useNavigate()

  // ✅ accessToken을 구독하여 변경 감지
  const accessToken = useAuthStore(state => state.accessToken)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true)

        // 디버깅 로그 추가
        logger.debug('[LoginPage] useEffect 실행 - accessToken:', accessToken ? '있음' : '없음')

        const result = await checkAuthStatus()

        // 로그인 페이지에서는 이미 인증되어 있으면 메인 페이지로 이동
        if (result.authenticated) {
          logger.auth('[LoginPage] 이미 인증됨 - 리다이렉트')
          if (result.needsRedirect && result.redirectPath) {
            navigate(result.redirectPath)
          } else {
            navigate('/')
          }
        }
      } catch (error) {
        logger.error('[LoginPage] 인증 체크 중 에러:', error)
        // 에러가 발생해도 로그인 페이지는 사용 가능해야 함
      } finally {
        // finally 블록으로 항상 isLoading이 false가 되도록 보장
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [navigate, accessToken])

  // 공통 토큰 처리 및 리다이렉트 함수
  const handleLoginSelect = useCallback(
    async (provider: Provider) => {
      // 이미 로딩 중이면 무시
      if (isLoading) {
        logger.warn('로그인 진행 중 - 중복 요청 무시')
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // WebView에서 Google 로그인 시 네이티브 인증 사용
        if (provider === 'google' && isWebView()) {
          logger.auth('[Google Login] WebView 환경에서 네이티브 인증 시작')

          // 브릿지 상태 확인
          logger.debug('[Google Login] Bridge 상태 확인:', {
            hasBridge: !!window.ReactNativeBridge,
            methods: window.ReactNativeBridge ? Object.keys(window.ReactNativeBridge) : [],
            sendMessageAsyncType: window.ReactNativeBridge ? typeof window.ReactNativeBridge.sendMessageAsync : 'undefined'
          })

          // 네이티브 Google Sign-In 수행
          const nativeResult = await signInWithGoogleNative()
          logger.auth('[Google Login] 네이티브 Sign-In 결과 수신:', nativeResult)

          // 취소된 경우 체크
          if (nativeResult.cancelled || nativeResult.user?.cancelled) {
            logger.warn('[Google Login] 사용자가 로그인을 취소했습니다.')
            return
          }

          // 네이티브에서 받은 idToken을 사용하여 백엔드에서 인증 처리
          logger.debug('[Google Login] nativeResult 구조 확인:', {
            hasUser: !!nativeResult.user,
            userType: nativeResult.user?.type,
            hasUserData: !!nativeResult.user?.data,
            hasIdToken: !!nativeResult.user?.data?.idToken,
            hasServerAuthCode: !!nativeResult.user?.data?.serverAuthCode,
            directIdToken: !!nativeResult.idToken,
            directAccessToken: !!nativeResult.accessToken
          })

          // 브릿지 데이터 구조에 맞게 토큰 추출
          const actualIdToken = nativeResult.user?.data?.idToken || nativeResult.idToken
          const actualAccessToken = nativeResult.user?.data?.serverAuthCode || nativeResult.accessToken

          logger.token('[Google Login] 토큰 추출 완료:', {
            hasIdToken: !!actualIdToken,
            hasAccessToken: !!actualAccessToken,
            idTokenLength: actualIdToken?.length || 0,
            accessTokenLength: actualAccessToken?.length || 0
          })

          // 토큰이 없으면 조기 실패
          if (!actualIdToken) {
            logger.error('[Google Login] idToken이 없습니다. nativeResult 구조를 확인하세요.')
            throw new Error('Google 인증 토큰을 받지 못했습니다.')
          }

          const callbackResult = await getNativeTokenCallbackApi('google', {
            idToken: actualIdToken,
            accessToken: actualAccessToken
          })

          // 백엔드 응답 검증
          logger.auth('[Google Login] 백엔드 응답 수신:', {
            hasData: !!callbackResult.data,
            hasAccessToken: !!callbackResult.data?.access_token
          })

          if (!callbackResult.data || !callbackResult.data.access_token) {
            logger.error('[Google Login] 백엔드 응답에 access_token이 없습니다:', {
              hasData: !!callbackResult.data,
              dataKeys: callbackResult.data ? Object.keys(callbackResult.data) : []
            })
            throw new Error('백엔드 인증에 실패했습니다')
          }

          logger.success('[Google Login] 백엔드 인증 성공')

          const { access_token, refresh_token, user } = callbackResult.data

          // 1. 메모리(Zustand)에 토큰 저장
          logger.info('[Google Login] 메모리에 토큰 저장 중...')
          useAuthStore.getState().setAccessToken(access_token)
          useAuthStore.getState().setRefreshToken(refresh_token)
          useAuthStore.getState().setUserInfo(user)
          logger.success('[Google Login] 토큰 메모리 저장 완료')

          // 2. RN에 토큰 백업 (비동기, 응답 기다리지 않음)
          if (window.ReactNativeBridge) {
            window.ReactNativeBridge.sendMessage('backupTokens', {
              accessToken: access_token,
              refreshToken: refresh_token,
              userInfo: user
            })
            logger.success('[Google Login] RN에 토큰 백업 요청 완료')
          }

          // 3. 리다이렉트 (navigate 사용하여 메모리 유지)
          if (user.is_agree) {
            logger.auth('[Google Login] 회원가입 완료 → 홈으로 이동')
            navigate('/')
          } else {
            logger.auth('[Google Login] 회원가입 미완료 → TOS 페이지로 이동')
            navigate(`/auth/tos?provider=${user.provider}&user_id=${user.id}`)
          }
          return
        }

        // WebView에서 Apple 로그인 시 네이티브 인증 사용 (iOS만)
        // Android에서는 에러가 발생하므로 iOS에서만 네이티브 Apple 인증 사용
        if (provider === 'apple' && isIOSWebView()) {
          logger.auth('[Apple Login] WebView 환경에서 네이티브 인증 시작')

          // 네이티브 Apple Sign-In 수행
          logger.debug('[Apple Login] signInWithAppleNative() 호출 중...')
          const nativeResult = await signInWithAppleNative()
          logger.auth('[Apple Login] 네이티브 Sign-In 결과 수신:', nativeResult)

          // 취소된 경우 체크
          if (nativeResult.cancelled) {
            logger.warn('[Apple Login] 사용자가 로그인을 취소했습니다.')
            return
          }

          logger.token('[Apple Login] 토큰 확인:', {
            hasIdentityToken: !!nativeResult.identityToken,
            hasAuthCode: !!nativeResult.authorizationCode
          })

          // 백엔드 API 요청 데이터 구성
          const apiRequestData = {
            idToken: nativeResult.identityToken,
            accessToken: nativeResult.authorizationCode
          }
          logger.debug('[Apple Login] 백엔드 API 요청 데이터 준비 완료')

          // 네이티브에서 받은 identityToken을 사용하여 백엔드에서 인증 처리
          logger.api('[Apple Login] getNativeTokenCallbackApi() 호출 중...')
          const callbackResult = await getNativeTokenCallbackApi('apple', apiRequestData)
          logger.auth('[Apple Login] 백엔드 API 응답 수신')

          // 백엔드 응답 검증
          logger.debug('[Apple Login] 백엔드 응답 구조 확인:', {
            hasData: !!callbackResult.data,
            hasAccessToken: !!(callbackResult.data && callbackResult.data.access_token)
          })

          if (!callbackResult.data || !callbackResult.data.access_token) {
            logger.error('[Apple Login] 백엔드 인증 실패 - access_token 없음')
            throw new Error('백엔드 인증에 실패했습니다')
          }

          logger.success('[Apple Login] 백엔드 인증 성공!')

          const { access_token, refresh_token, user } = callbackResult.data

          // 1. 메모리(Zustand)에 토큰 저장
          logger.info('[Apple Login] 메모리에 토큰 저장 중...')
          useAuthStore.getState().setAccessToken(access_token)
          useAuthStore.getState().setRefreshToken(refresh_token)
          useAuthStore.getState().setUserInfo(user)
          logger.success('[Apple Login] 토큰 메모리 저장 완료')

          // 2. RN에 토큰 백업 (비동기, 응답 기다리지 않음)
          if (window.ReactNativeBridge) {
            window.ReactNativeBridge.sendMessage('backupTokens', {
              accessToken: access_token,
              refreshToken: refresh_token,
              userInfo: user
            })
            logger.success('[Apple Login] RN에 토큰 백업 요청 완료')
          }

          // 3. 리다이렉트 (navigate 사용하여 메모리 유지)
          if (user.is_agree) {
            logger.auth('[Apple Login] 회원가입 완료 → 홈으로 이동')
            navigate('/')
          } else {
            logger.auth('[Apple Login] 회원가입 미완료 → TOS 페이지로 이동')
            navigate(`/auth/tos?provider=${user.provider}&user_id=${user.id}`)
          }
          return
        }

        // 일반 웹 브라우저에서의 기존 OAuth 플로우
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 700))
        let response
        if (provider === 'apple') {
          response = await getWithRedirectApi(provider, 'https://qbee.eduplex.net/auth/apple/bridge/callback')
        } else {
          response = await getWithRedirectApi(provider, `${window.location.origin}/auth/signup-check/${provider}`)
        }

        const auth_url = response?.data?.auth_url
        if (!auth_url) {
          logger.error(`[${provider} Login] auth_url이 없습니다.`, response)
          throw new Error(`${provider} 로그인 URL을 받지 못했습니다.`)
        }

        logger.auth(`[${provider} Login] OAuth URL로 리다이렉트:`, auth_url)
        window.location.href = auth_url
      } catch (error: any) {
        logger.error(`[${provider} Login] 로그인 실패:`, {
          errorType: typeof error,
          errorName: error.name,
          errorMessage: error.message,
          responseStatus: error.response?.status,
          responseData: error.response?.data
        })

        showErrorToast(`${provider} 로그인 중 오류가 발생했습니다.`)
        setError(`${provider} 로그인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.`)
      } finally {
        setIsLoading(false)
      }
    },
    [isLoading, showErrorToast, navigate]
  )

  const handleRetryLogin = useCallback(
    (method: Provider) => {
      setError(null)
      handleLoginSelect(method)
    },
    [handleLoginSelect]
  )

  return (
    <div className='flex flex-col min-h-screen relative font-sans noto-sans-kr'>
      <div className='flex-1 flex flex-col items-center justify-center space-y-3 px-6'>
        <h1 className='text-5xl font-bold bg-gradient-to-r from-amber-500 to-amber-700 inline-block text-transparent bg-clip-text h-20'>
          qBee
        </h1>
      </div>

      <LoginButtons onLoginSelect={handleLoginSelect} isLoading={isLoading} error={error} onRetry={handleRetryLogin} />
    </div>
  )
}
